import { useEffect } from "react";
import { useHistory } from "react-router-dom";

type TimesheetStep = "timesheet-upload" | "facility-signing" | "hcp-signing" | "qr-code";

interface TimesheetHistoryState {
  step?: TimesheetStep;
}

interface UseTimesheetStepParams {
  isQrCodeSubmission?: boolean;
  isDigitalTimesheetEnabled: boolean;
}

interface UseTimesheetStepReturn {
  currentStep: TimesheetStep;
  setCurrentStep: (step: TimesheetStep, options?: { addToHistory?: boolean }) => void;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isTimesheetHistoryState(state: unknown): state is TimesheetHistoryState {
  if (!isObject(state)) {
    return false;
  }

  if ("step" in state) {
    return isString(state.step) || state.step === undefined;
  }

  return true;
}

export function useTimesheetStep({
  isQrCodeSubmission,
  isDigitalTimesheetEnabled,
}: UseTimesheetStepParams): UseTimesheetStepReturn {
  const history = useHistory();

  // Get current step from history state, fallback to default
  let currentStep: TimesheetStep;

  if (isTimesheetHistoryState(history.location.state) && history.location.state.step) {
    currentStep = history.location.state.step;
  } else {
    currentStep = isDigitalTimesheetEnabled ? "facility-signing" : "timesheet-upload";
  }

  useSetInitialTimesheetStep({
    isQrCodeSubmission,
    isDigitalTimesheetEnabled,
    history,
  });

  // Handle step changes with history navigation
  const setCurrentStep = (step: TimesheetStep, options?: { addToHistory?: boolean }) => {
    const shouldAddToHistory = options?.addToHistory ?? true;
    if (shouldAddToHistory) {
      history.push({ ...history.location, state: { step } });
    } else {
      history.replace({ ...history.location, state: { step } });
    }
  };

  return {
    currentStep,
    setCurrentStep,
  };
}

interface UseSetInitialTimesheetStepParams {
  isQrCodeSubmission?: boolean;
  isDigitalTimesheetEnabled: boolean;
  history: ReturnType<typeof useHistory>;
}

function useSetInitialTimesheetStep({
  isQrCodeSubmission,
  isDigitalTimesheetEnabled,
  history,
}: UseSetInitialTimesheetStepParams): void {
  useEffect(() => {
    // Determine what the step should be based on configuration
    let expectedStep: TimesheetStep;
    if (isQrCodeSubmission === true) {
      expectedStep = "qr-code";
    } else if (isDigitalTimesheetEnabled) {
      expectedStep = "facility-signing";
    } else {
      expectedStep = "timesheet-upload";
    }

    // Only update if the current step doesn't match what it should be
    if (
      isTimesheetHistoryState(history.location.state) &&
      history.location.state.step === expectedStep
    ) {
      return;
    }

    // Use replace to avoid creating extra history entries
    history.replace({ ...history.location, state: { step: expectedStep } });
  }, [isDigitalTimesheetEnabled, isQrCodeSubmission, history]);
}

export type { TimesheetStep };
